import { computed, inject, Injectable, linkedSignal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, tap } from 'rxjs';

import { CreateEntityConfigService } from './config/create-entity-config.service';
import { EntityTab } from './create-entity-element.types';
import { CreateEntityWizardStore } from './create-entity-wizard.store';
import { JemmaOrganizationApiService } from './organization/jemma-organization-api.service';
import { JemmaStudyApiService } from './study/jemma-study-api.service';

/**
 * Owns the codeset lookups (countries, organization types, study statuses) shared by the entity
 * forms, so a form never holds its own `rxResource` and countries are fetched once for both the
 * Organization and Facility tabs. Provided per-`<wcg-create-entity>` instance, like
 * `CreateEntityConfigService`.
 *
 * Each lookup stays idle until `_config.$isUsable()` turns true, then fetches exactly once — a later
 * config change (for example a refreshed token) never refetches. The stream reads the config at
 * request time, so it always sends the host's current token.
 */
@Injectable()
export class CreateEntityReferenceDataService {
    private readonly _config = inject(CreateEntityConfigService);
    private readonly _wizard = inject(CreateEntityWizardStore);
    private readonly _organizationApi = inject(JemmaOrganizationApiService);
    private readonly _studyApi = inject(JemmaStudyApiService);

    private readonly _countries = this._createGatedResource(() => this._isShown('organization') || this._isShown('facility'), () => this._organizationApi.getCountries(this._config.$config()));
    private readonly _organizationTypes = this._createGatedResource(() => this._isShown('organization'), () => this._organizationApi.getOrganizationTypes(this._config.$config()));
    private readonly _studyStatuses = this._createGatedResource(() => this._isShown('study'), () => this._studyApi.getStudyStatuses(this._config.$config()));

    readonly $countries = computed(() => (this._countries.hasValue() ? this._countries.value() : []));
    readonly $organizationTypes = computed(() => (this._organizationTypes.hasValue() ? this._organizationTypes.value() : []));
    readonly $studyStatuses = computed(() => (this._studyStatuses.hasValue() ? this._studyStatuses.value() : []));

    readonly $countriesError = computed(() => this._countries.status() === 'error');
    readonly $organizationTypesError = computed(() => this._organizationTypes.status() === 'error');
    readonly $studyStatusesError = computed(() => this._studyStatuses.status() === 'error');

    /**
     * Idle until the config is usable and a tab that needs this codeset is shown, then fetches. `params` is the access token held in a `linkedSignal`,
     * so a failed fetch is retried when the token changes (for example a refresh). Once a fetch succeeds,
     * the linked token keeps its previous value, so later config changes never refetch and never drop the
     * loaded value — an `rxResource` reloads whenever `params` re-evaluates, so the gate has to sit in this
     * signal rather than in `params` itself.
     */
    private _createGatedResource<T>(isNeeded: () => boolean, request: () => Observable<T[]>) {
        let _hasSucceeded = false;

        const _token = linkedSignal<string | undefined, string | undefined>({
            source: () => (isNeeded() && this._config.$isUsable() ? this._config.$config().accessToken : undefined),
            computation: (source, previous) => (_hasSucceeded && previous ? previous.value : source)
        });

        return rxResource({
            params: () => _token(),
            stream: () =>
                request().pipe(
                    tap(() => {
                        _hasSucceeded = true;
                    })
                )
        });
    }

    private _isShown(tab: EntityTab): boolean {
        return this._wizard.visibleTabs().includes(tab);
    }
}
